// Artwork Approval — proof previews + decisions, broken out per piece of
// submitted media. Each design card shows one row per gang sheet (DTF) or
// per printed garment side (garment). Admin signs off (or sends back for
// revision / rejects) ONE piece at a time; the design-level aggregate is
// rolled up server-side so Production + customer-facing surfaces still see
// a single overall status.
//
// Each piece row carries the rendering payload the API derives from the
// design.spec — a Fabric.js canvas JSON for the artwork, the mockup URL
// (garment only), and the print zone (garment only) — so the page can
// render a real thumbnail of the actual submitted media + a full-page
// lightbox via the shared MockupCanvases components.
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch, useApiSrc } from '../../lib/api'
import { DtfSheetCanvas, GarmentSideCanvas, MockupLightbox, GarmentSideProof, DtfSheetProof } from '../../components/admin/MockupCanvases'

type PieceStatus = 'pending' | 'approved' | 'revision_requested' | 'rejected'

interface PieceRender {
  sheet?: {
    canvas: object | null
    lengthCm: number | null
    placements: Array<{ label: string; qty?: number | null; printWidthMm?: number | null; printHeightMm?: number | null }> | null
  }
  garment?: {
    canvas: object | null
    mockupUrl: string | null
    printZone: { left: number; top: number; width: number; height: number }
    printWidthMm: number
  }
}
interface PieceFile {
  fileId: string
  originalName: string | null
  mimeType: string | null
  size: number
  widthPx: number | null
  heightPx: number | null
  printWidthMm: number | null
  printHeightMm: number | null
  label: string | null
  qty: number | null
  url: string
}
interface ArtworkPiece {
  id: string
  kind: 'sheet' | 'location' | string
  key: string
  label: string
  status: PieceStatus
  notes: string | null
  approvedAt: string | null
  // Signed URL for the flat PNG baked at submission. Null on legacy
  // submissions; falls back to live Fabric rendering in that case.
  thumbnailUrl: string | null
  render: PieceRender
  // Customer-uploaded ORIGINAL artwork files placed on this piece. The
  // View button opens these at their full resolution + lets the admin
  // download them.
  files: PieceFile[]
}
interface ArtworkOrderRef {
  id: string
  number: number
  status: string
}
interface ArtworkRow {
  id: string; kind: string; name: string | null
  approvalStatus: PieceStatus; approvalNotes: string | null
  approvedAt: string | null; updatedAt: string; priceCents: number | null
  proofUrl: string | null
  customerName: string | null; customerEmail: string | null
  orderId: string | null
  orderNumber: number | null
  orders: ArtworkOrderRef[]
  pieces: ArtworkPiece[]
}
type Counts = { pending: number; approved: number; revision_requested: number; rejected: number }

const TABS: Array<{ key: keyof Counts; label: string }> = [
  { key: 'pending',             label: 'Pending' },
  { key: 'approved',            label: 'Approved' },
  { key: 'revision_requested',  label: 'Revision requested' },
  { key: 'rejected',            label: 'Rejected' },
]

const STATUS_CHIP: Record<PieceStatus, { label: string; cls: string }> = {
  pending:            { label: 'Pending',  cls: 'bg-surface text-ink-3 border-border' },
  approved:           { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  revision_requested: { label: 'Revision', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  rejected:           { label: 'Rejected', cls: 'bg-red-100 text-red-700 border-red-200' },
}

export default function ArtworkApproval() {
  const { getAccessToken } = useAuth()
  const apiSrc = useApiSrc()
  const [tab, setTab] = useState<keyof Counts>('pending')
  const [items, setItems] = useState<ArtworkRow[]>([])
  const [counts, setCounts] = useState<Counts>({ pending: 0, approved: 0, revision_requested: 0, rejected: 0 })
  const [err, setErr] = useState<string | null>(null)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [notes, setNotes] = useState<Record<string, string>>({})
  // Lightbox state — the piece being viewed full-page.
  const [lightbox, setLightbox] = useState<{ row: ArtworkRow; piece: ArtworkPiece } | null>(null)
  // Light / Dark preview backdrop for the proof inside the lightbox —
  // mirrors the Order Detail Proof toggle so the proof renders identically
  // on both surfaces.
  const [proofBg, setProofBg] = useState<'white' | 'grey' | 'black'>('white')

  const load = useCallback(async () => {
    try {
      const [list, c] = await Promise.all([
        apiFetch<{ items: ArtworkRow[] }>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/artwork?status=${tab}`),
        apiFetch<Counts>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/artwork/counts'),
      ])
      setItems(list.items); setCounts(c); setErr(null)
    } catch (e) { setErr((e as Error).message) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])
  useEffect(() => { void load() }, [load])

  async function decidePiece(designId: string, pieceKey: string, status: PieceStatus) {
    const noteKey = `${designId}:${pieceKey}`
    const noteVal = (notes[noteKey] ?? '').trim()
    if (status === 'revision_requested' && !noteVal) {
      alert('Add a note before requesting a revision — the customer needs to know what to change.')
      return
    }
    setBusyKey(noteKey)
    try {
      await apiFetch(
        getAccessToken as unknown as () => Promise<string | undefined>,
        `/api/admin/artwork/${designId}/pieces/${encodeURIComponent(pieceKey)}`,
        { method: 'PATCH', body: JSON.stringify({ status, notes: noteVal || undefined }) },
      )
      setNotes((n) => { const next = { ...n }; delete next[noteKey]; return next })
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setBusyKey(null) }
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="eyebrow">Production</div>
        <h1 className="font-heading uppercase font-bold text-3xl text-ink">Artwork Approval</h1>
        <p className="text-ink-2 mt-2 text-sm max-w-2xl">Sign off on customer-submitted proofs before they enter the print queue. <strong>Every piece of submitted media gets its own decision</strong> — each DTF gang sheet, each printed garment side. The design-level status rolls up automatically.</p>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5 border-b border-border pb-1">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={'px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded transition-colors ' +
              (tab === t.key ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>
            {t.label} <span className="ml-1 text-ink-3">{counts[t.key]}</span>
          </button>
        ))}
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      {items.length === 0 ? (
        <div className="card text-center text-ink-3 text-sm">Nothing in this bucket.</div>
      ) : (() => {
        // Flatten: one card per piece so each side/sheet can move through the
        // approval process independently. Designs with no pieces still get a
        // placeholder card so operators know something needs attention.
        const allPieces: Array<{ row: ArtworkRow; piece: ArtworkPiece }> = []
        for (const r of items) {
          if (r.pieces.length === 0) {
            // Synthesise a placeholder piece so the design still surfaces.
            allPieces.push({
              row: r,
              piece: {
                id: `${r.id}:__none`,
                kind: 'unknown',
                key: '__none',
                label: '—',
                status: r.approvalStatus,
                notes: null,
                approvedAt: null,
                thumbnailUrl: null,
                render: {},
                files: [],
              } satisfies ArtworkPiece,
            })
          } else {
            r.pieces.forEach((p) => allPieces.push({ row: r, piece: p }))
          }
        }
        return (
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-5">
            {allPieces.map(({ row: r, piece: p }) => {
              const noteKey = `${r.id}:${p.key}`
              const busy = busyKey === noteKey
              const isPlaceholder = p.key === '__none'
              return (
                <div key={`${r.id}:${p.id}`} className="card flex flex-col gap-3">

                  {/* ── Card header: side label + status + customer ── */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-ink-3">{r.kind} · {p.kind === 'sheet' ? 'Sheet' : p.kind === 'location' ? 'Side' : 'Unknown'}</span>
                        <span className={'inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ' + STATUS_CHIP[p.status].cls}>
                          {STATUS_CHIP[p.status].label}
                        </span>
                        {p.kind === 'location' && p.render.garment && (
                          <span className="text-[10px] text-ink-3 font-mono">~{p.render.garment.printWidthMm} mm</span>
                        )}
                        {p.kind === 'sheet' && p.render.sheet?.lengthCm && (
                          <span className="text-[10px] text-ink-3 font-mono">60 x {p.render.sheet.lengthCm} cm</span>
                        )}
                      </div>
                      <h3 className="text-base font-bold text-ink leading-tight mt-0.5 truncate">{p.label}</h3>
                      <div className="text-sm text-ink-2 truncate mt-0.5">{r.customerName ?? r.customerEmail ?? '—'}</div>
                      <div className="text-[11px] text-ink-3 truncate">{r.customerEmail}</div>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1">
                      {r.orders.map((o) => (
                        <Link key={o.id} to={`/admin/orders/${o.id}`}
                          title={`Order #${o.number} — ${o.status.replace(/_/g, ' ')}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gold/10 border border-gold/40 text-[11px] font-bold text-gold-dark hover:bg-gold/20 hover:underline font-mono">
                          #{o.number}
                        </Link>
                      ))}
                      {r.priceCents != null && <div className="text-[11px] text-ink-3 font-mono">${(r.priceCents / 100).toFixed(2)}</div>}
                    </div>
                  </div>

                  {/* ── Thumbnail (click → lightbox) ── */}
                  {isPlaceholder ? (
                    <div className="text-xs text-ink-3 italic text-center border border-dashed border-border rounded p-4">
                      No individual pieces detected. Use the order detail to inspect.
                    </div>
                  ) : (
                    <button onClick={() => setLightbox({ row: r, piece: p })}
                      className="block w-full focus:outline-none focus:ring-2 focus:ring-gold rounded overflow-hidden mx-auto"
                      style={{ maxWidth: 220 }}
                      title={`View ${p.label} full-size`}>
                      {p.thumbnailUrl ? (
                        <img src={apiSrc(p.thumbnailUrl)} alt={`${p.label} mockup`}
                          className="w-full h-auto block border border-border rounded bg-surface" />
                      ) : p.kind === 'sheet' && p.render.sheet ? (
                        <DtfSheetCanvas
                          sheet={{ label: p.label, canvas: p.render.sheet.canvas, lengthCm: p.render.sheet.lengthCm, placements: p.render.sheet.placements ?? null }}
                          apiSrc={apiSrc} mode="thumb" />
                      ) : p.kind === 'location' && p.render.garment ? (
                        <GarmentSideCanvas
                          mockupUrl={p.render.garment.mockupUrl}
                          canvas={p.render.garment.canvas}
                          apiSrc={apiSrc} mode="full" />
                      ) : (
                        <div className="aspect-[4/5] bg-surface border border-border rounded flex items-center justify-center text-[11px] text-ink-3">
                          No preview
                        </div>
                      )}
                    </button>
                  )}

                  {/* ── Existing notes + note input ── */}
                  {p.notes && <p className="text-xs text-ink-2 bg-surface border border-border rounded p-2">{p.notes}</p>}
                  {!isPlaceholder && (
                    <textarea
                      value={notes[noteKey] ?? ''}
                      onChange={(e) => setNotes((n) => ({ ...n, [noteKey]: e.target.value }))}
                      rows={2} placeholder={`Note for the customer about ${p.label.toLowerCase()} (required to request a revision)`}
                      className="input" />
                  )}

                  {/* ── Decision buttons ── */}
                  {!isPlaceholder && (
                    <div className="mt-auto space-y-2">
                      <div className="grid grid-cols-4 gap-2">
                        <button onClick={() => setLightbox({ row: r, piece: p })}
                          className="px-3 py-2 text-xs font-semibold uppercase tracking-wide rounded bg-ink text-white hover:bg-ink-2 transition-colors flex items-center justify-center gap-1.5"
                          title="Open this piece in a full-page lightbox">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                          </svg>
                          View
                        </button>
                        <button disabled={busy} onClick={() => decidePiece(r.id, p.key, 'approved')}
                          className="px-3 py-2 text-xs font-semibold uppercase tracking-wide rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors">Approve</button>
                        <button
                          disabled={busy || !(notes[noteKey] ?? '').trim()}
                          onClick={() => decidePiece(r.id, p.key, 'revision_requested')}
                          title={(notes[noteKey] ?? '').trim() ? 'Send this piece back for revision with the note above' : 'Add a note above before requesting a revision'}
                          className="px-3 py-2 text-xs font-semibold uppercase tracking-wide rounded bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Revision</button>
                        <button disabled={busy} onClick={() => decidePiece(r.id, p.key, 'rejected')}
                          className="px-3 py-2 text-xs font-semibold uppercase tracking-wide rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors">Reject</button>
                      </div>
                      {p.status !== 'pending' && (
                        <div className="flex justify-end">
                          <button disabled={busy} onClick={() => decidePiece(r.id, p.key, 'pending')}
                            className="text-[11px] font-semibold uppercase tracking-wide text-ink-3 hover:text-ink underline underline-offset-2 disabled:opacity-50 transition-colors"
                            title="Send this piece back to pending (use if you clicked approve/revision/reject by mistake).">
                            ↶ Move back to pending
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )
      })()}

      {/* Lightbox — renders the EXACT same per-piece proof the admin Order
          Detail Proof section shows: full-page transparent A3 proof with
          customer artwork at print-zone-accurate positions for garment
          sides, or the white/black-substrate DTF sheet for DTF pieces.
          Includes the same Light/Dark backdrop toggle and the same
          PNG/PDF download split button. Below the proof we still surface
          the original uploaded files (with native-resolution viewer +
          download link) for additional inspection. */}
      <MockupLightbox
        open={!!lightbox}
        eyebrow="Proof — print-ready"
        title={lightbox?.piece.label ?? ''}
        subtitle={
          lightbox
            ? (lightbox.piece.kind === 'location' && lightbox.piece.render.garment
                ? `${lightbox.piece.render.garment.printWidthMm.toFixed(0)} mm wide · transparent`
                : lightbox.piece.kind === 'sheet' && lightbox.piece.render.sheet?.lengthCm
                  ? `60 x ${lightbox.piece.render.sheet.lengthCm} cm`
                  : '')
            : ''
        }
        onClose={() => setLightbox(null)}>
        {lightbox && (
          <div className="space-y-6">
            {/* Light / Dark backdrop toggle — preview only. The exported
                PNG always carries full alpha for garment proofs; the DTF
                proof bakes the substrate colour in. */}
            <div className="flex items-center justify-end gap-2 text-[10px] uppercase tracking-wider font-bold text-ink-3"
                 title="Preview backdrop only — garment proof exports stay fully transparent">
              <span>Preview on</span>
              <div className="inline-flex border border-border rounded overflow-hidden">
                <button onClick={() => setProofBg('white')} aria-pressed={proofBg === 'white'}
                  className={'px-2.5 py-1 transition-colors ' + (proofBg === 'white' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>White</button>
                <button onClick={() => setProofBg('grey')} aria-pressed={proofBg === 'grey'}
                  className={'px-2.5 py-1 border-l border-border transition-colors ' + (proofBg === 'grey' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>Grey</button>
                <button onClick={() => setProofBg('black')} aria-pressed={proofBg === 'black'}
                  className={'px-2.5 py-1 border-l border-border transition-colors ' + (proofBg === 'black' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>Black</button>
              </div>
            </div>

            {/* The proof itself — same component as Order Detail Proof. */}
            {lightbox.piece.kind === 'location' && lightbox.piece.render.garment ? (
              <GarmentSideProof
                side={{
                  name: lightbox.piece.label,
                  canvas: lightbox.piece.render.garment.canvas,
                  printZone: lightbox.piece.render.garment.printZone,
                  printWidthMm: lightbox.piece.render.garment.printWidthMm,
                }}
                proofBg={proofBg}
                orderNumber={lightbox.row.orderNumber ?? lightbox.row.id} />
            ) : lightbox.piece.kind === 'sheet' && lightbox.piece.render.sheet ? (
              <DtfSheetProof
                sheet={{
                  label: lightbox.piece.label,
                  canvas: lightbox.piece.render.sheet.canvas,
                  lengthCm: lightbox.piece.render.sheet.lengthCm,
                }}
                proofBg={proofBg}
                orderNumber={lightbox.row.orderNumber ?? lightbox.row.id} />
            ) : lightbox.piece.thumbnailUrl ? (
              <div className="mx-auto" style={{ maxWidth: 760 }}>
                <p className="text-xs text-ink-3 mb-3 text-center italic">No proof render available — showing the baked mockup composite.</p>
                <img src={apiSrc(lightbox.piece.thumbnailUrl)} alt={`${lightbox.piece.label} mockup`}
                  className="w-full h-auto block border border-border rounded bg-surface" />
              </div>
            ) : null}

            {/* Original uploaded files — kept under the proof for full-
                resolution inspection + downloading. Render at native 1:1
                inside a scrollable box. */}
            {lightbox.piece.files.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 mb-2">
                  Original file{lightbox.piece.files.length === 1 ? '' : 's'} ({lightbox.piece.files.length})
                </div>
                <div className="space-y-3">
                  {lightbox.piece.files.map((f) => (
                    <div key={f.fileId} className="border border-border rounded overflow-hidden bg-white">
                      <div className="px-4 py-2 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-ink truncate">{f.label ?? f.originalName ?? 'Artwork'}</div>
                          <div className="text-[11px] text-ink-3 mt-0.5 flex items-center gap-3 flex-wrap font-mono">
                            {f.widthPx && f.heightPx && <span>{f.widthPx} x {f.heightPx} px</span>}
                            {(f.printWidthMm || f.printHeightMm) && <span>{f.printWidthMm ?? '?'} x {f.printHeightMm ?? '?'} mm</span>}
                            <span>{formatBytes(f.size)}</span>
                            {f.qty != null && f.qty > 1 && <span>x {f.qty}</span>}
                            {f.mimeType && <span className="uppercase">{f.mimeType.split('/')[1] ?? f.mimeType}</span>}
                          </div>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          <a href={apiSrc(f.url)} target="_blank" rel="noreferrer"
                            title="Open the original file in a new tab at native resolution"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded border border-border text-ink-2 hover:border-gold hover:text-ink transition-colors whitespace-nowrap">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                            Open
                          </a>
                          <a href={apiSrc(f.url)} download={f.originalName ?? undefined}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-ink text-white hover:bg-ink-2 transition-colors whitespace-nowrap">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                            Download
                          </a>
                        </div>
                      </div>
                      <div className="overflow-auto bg-[conic-gradient(at_50%_50%,#eee_25%,#fff_0_50%,#eee_0_75%,#fff_0)] bg-[length:16px_16px]"
                           style={{ maxHeight: '50vh' }}>
                        <img src={apiSrc(f.url)} alt={f.label ?? f.originalName ?? 'Artwork'}
                          className="block" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </MockupLightbox>
    </div>
  )
}

// Pretty-print a file size for the lightbox metadata row. KB / MB / GB
// thresholds match the rest of the admin chrome (1024-based).
function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '?'
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let v = bytes / 1024
  let i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`
}
